import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import api from "../lib/api";
import { Card, CardContent } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Badge } from "./ui/badge";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "./ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";
import { Textarea } from "./ui/textarea";
import { toast } from "sonner";
import { Database, Lock, Unlock, Plus, Trash2, Edit3, Search, Loader2, RefreshCcw, LayoutList, Code2 } from "lucide-react";
import ProtocolForm from "./ProtocolForm.jsx";

const RESOURCES = [
  { key: "protocols",         label: "Protocols",         displayFields: ["title", "category", "severity"] },
  { key: "drugs",             label: "Drugs",             displayFields: ["name", "drug_class", "category"] },
  { key: "drug_interactions", label: "Interactions",      displayFields: ["drug1", "drug2", "severity"] },
  { key: "calculators",       label: "Calculators",       displayFields: ["name", "category"] },
  { key: "icd10_codes",       label: "ICD-10",            displayFields: ["code", "description"] },
  { key: "paediatric_drugs",  label: "Paed Drugs",        displayFields: ["name", "indication"] },
  { key: "antibiotic_guide",  label: "Antibiotic guide",  displayFields: ["condition", "first_line"] },
  { key: "quick_reference",   label: "Quick reference",   displayFields: ["title", "category"] },
];

function ResourceManager({ resource }) {
  const { key, displayFields } = resource;
  const [state, setState] = useState({ total: 0, items: [], id_field: "id" });
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const pageSize = 50;

  const [editing, setEditing] = useState(null); // { mode: "create"|"edit", original, text, doc, useForm }
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(null);

  const qRef = useRef(q);
  useEffect(() => { qRef.current = q; }, [q]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/cms/${key}`, {
        params: { q: qRef.current || undefined, skip: page * pageSize, limit: pageSize },
      });
      setState(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || `Failed to load ${key}`);
    } finally {
      setLoading(false);
    }
  }, [key, page, pageSize]);

  useEffect(() => { load(); }, [load]);

  const search = (e) => { e.preventDefault(); setPage(0); load(); };

  const openCreate = () => {
    const blank = key === "protocols"
      ? { id: "", protocol_id: "", title: "", category: "", severity: "moderate", status: "draft", algorithm_steps: [], red_flags: [] }
      : { [state.id_field]: "" };
    setEditing({
      mode: "create",
      original: null,
      doc: blank,
      text: JSON.stringify(blank, null, 2),
      useForm: key === "protocols",
    });
  };

  const openEdit = async (item) => {
    try {
      const { data } = await api.get(`/admin/cms/${key}/${encodeURIComponent(item[state.id_field])}`);
      setEditing({
        mode: "edit",
        original: data,
        doc: data,
        text: JSON.stringify(data, null, 2),
        useForm: key === "protocols",
      });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not load item");
    }
  };

  // Sync text <-> doc when toggling editor view
  const toggleEditorView = () => {
    if (!editing) return;
    if (editing.useForm) {
      // switching to JSON view — serialise the current doc
      setEditing({ ...editing, useForm: false, text: JSON.stringify(editing.doc, null, 2) });
    } else {
      // switching to form view — try to parse the text first
      try {
        const parsed = JSON.parse(editing.text);
        setEditing({ ...editing, useForm: true, doc: parsed });
      } catch {
        toast.error("Fix JSON syntax before switching back to the form view.");
      }
    }
  };

  const save = async () => {
    let parsed;
    if (editing.useForm) {
      parsed = editing.doc || {};
    } else {
      try { parsed = JSON.parse(editing.text); }
      catch { toast.error("Invalid JSON — fix syntax before saving."); return; }
    }
    setSaving(true);
    try {
      if (editing.mode === "create") {
        await api.post(`/admin/cms/${key}`, { doc: parsed });
        toast.success("Created");
      } else {
        const id = editing.original[state.id_field];
        await api.patch(`/admin/cms/${key}/${encodeURIComponent(id)}`, { doc: parsed });
        toast.success("Saved");
      }
      setEditing(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleting) return;
    try {
      await api.delete(`/admin/cms/${key}/${encodeURIComponent(deleting[state.id_field])}`);
      toast.success("Deleted");
      setDeleting(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    }
  };

  const toggleLock = async (item) => {
    const newState = !item.locked;
    try {
      await api.patch(`/admin/cms/${key}/${encodeURIComponent(item[state.id_field])}/lock`, { locked: newState });
      toast.success(newState ? "Locked from sync" : "Unlocked — sync may overwrite");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Toggle failed");
    }
  };

  const pageCount = Math.max(1, Math.ceil(state.total / pageSize));

  return (
    <Card className="border-slate-200" data-testid={`cms-${key}`}>
      <CardContent className="p-0">
        <div className="p-4 border-b border-slate-200 flex flex-col md:flex-row md:items-center gap-3 md:justify-between">
          <div className="flex items-center gap-2">
            <Database className="w-4 h-4 text-[#111827]" />
            <h3 className="font-heading font-semibold">{resource.label}</h3>
            <Badge variant="outline" className="text-xs">{state.total.toLocaleString()} total</Badge>
          </div>
          <div className="flex gap-2 w-full md:w-auto">
            <form onSubmit={search} className="flex gap-2 flex-1 md:flex-initial">
              <div className="relative flex-1 md:w-64">
                <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Search…"
                  className="pl-8 h-9 text-sm"
                  data-testid={`cms-${key}-search`}
                />
              </div>
              <Button type="submit" variant="outline" size="sm" data-testid={`cms-${key}-search-btn`}>Search</Button>
            </form>
            <Button onClick={() => load()} size="sm" variant="outline" title="Reload">
              <RefreshCcw className="w-3.5 h-3.5" />
            </Button>
            <Button onClick={openCreate} size="sm" className="bg-[#0B5FFF] hover:bg-[#0A54E0]" data-testid={`cms-${key}-create`}>
              <Plus className="w-3.5 h-3.5 mr-1" /> New
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="p-8 text-center text-slate-500 text-sm"><Loader2 className="w-4 h-4 animate-spin inline mr-2" />Loading…</div>
        ) : state.items.length === 0 ? (
          <div className="p-8 text-center text-slate-500 text-sm">No items found.</div>
        ) : (
          <Table>
            <TableHeader className="bg-slate-50">
              <TableRow>
                <TableHead className="font-mono text-xs">{state.id_field}</TableHead>
                {displayFields.map((f) => <TableHead key={f}>{f}</TableHead>)}
                <TableHead>Lock</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {state.items.map((item, i) => (
                <TableRow key={item[state.id_field] || i} className={i % 2 ? "bg-slate-50/50" : ""}>
                  <TableCell className="font-mono text-xs text-slate-600 truncate max-w-[180px]">{String(item[state.id_field] || "—")}</TableCell>
                  {displayFields.map((f) => (
                    <TableCell key={f} className="text-sm max-w-[280px] truncate">
                      {Array.isArray(item[f]) ? item[f].join(", ") : (item[f] ?? "—").toString()}
                    </TableCell>
                  ))}
                  <TableCell>
                    <button
                      onClick={() => toggleLock(item)}
                      className="inline-flex items-center gap-1"
                      title={item.locked ? "Locked — sync will NOT overwrite" : "Unlocked — sync may overwrite"}
                      data-testid={`cms-${key}-lock-${item[state.id_field]}`}
                    >
                      {item.locked ? (
                        <Badge className="bg-amber-100 text-amber-800 border-0 hover:bg-amber-200">
                          <Lock className="w-3 h-3 mr-1" /> Locked
                        </Badge>
                      ) : (
                        <Badge className="bg-slate-100 text-slate-600 border-0 hover:bg-slate-200">
                          <Unlock className="w-3 h-3 mr-1" /> Open
                        </Badge>
                      )}
                    </button>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-1">
                      <Button size="sm" variant="outline" onClick={() => openEdit(item)} data-testid={`cms-${key}-edit-${item[state.id_field]}`}>
                        <Edit3 className="w-3.5 h-3.5" />
                      </Button>
                      <Button size="sm" variant="outline" className="text-rose-700 hover:bg-rose-50" onClick={() => setDeleting(item)} data-testid={`cms-${key}-delete-${item[state.id_field]}`}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}

        {state.total > pageSize && (
          <div className="p-3 border-t border-slate-200 flex items-center justify-between text-sm">
            <div className="text-xs text-slate-500">Page {page + 1} of {pageCount}</div>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" disabled={page === 0} onClick={() => setPage((p) => Math.max(0, p - 1))}>Prev</Button>
              <Button size="sm" variant="outline" disabled={page + 1 >= pageCount} onClick={() => setPage((p) => p + 1)}>Next</Button>
            </div>
          </div>
        )}
      </CardContent>

      {/* Edit/create dialog */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <div className="flex items-center justify-between gap-3">
              <DialogTitle>
                {editing?.mode === "create" ? `New ${resource.label.slice(0, -1) || resource.label}` : `Edit ${resource.label.slice(0, -1) || resource.label}`}
              </DialogTitle>
              {key === "protocols" && (
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={toggleEditorView}
                  className="text-xs"
                  data-testid="cms-toggle-editor-view"
                >
                  {editing?.useForm ? <><Code2 className="w-3.5 h-3.5 mr-1" /> Raw JSON</> : <><LayoutList className="w-3.5 h-3.5 mr-1" /> Form view</>}
                </Button>
              )}
            </div>
          </DialogHeader>
          <div className="space-y-2">
            <div className="text-xs text-slate-500">
              {editing?.useForm
                ? `Friendly form editor for protocols. Switch to "Raw JSON" if you need to edit fields not exposed here.`
                : `Full record as JSON. Any item saved here gets `}
              {!editing?.useForm && (
                <>
                  <code className="px-1 bg-slate-100 rounded">locked: true</code> and <strong>will not</strong> be overwritten by the periodic sync. ID field: <code className="px-1 bg-slate-100 rounded">{state.id_field}</code>.
                </>
              )}
            </div>
            {editing?.useForm ? (
              <ProtocolForm
                value={editing.doc}
                onChange={(doc) => setEditing((s) => ({ ...s, doc }))}
              />
            ) : (
              <Textarea
                value={editing?.text || ""}
                onChange={(e) => setEditing((s) => ({ ...s, text: e.target.value }))}
                rows={18}
                className="font-mono text-xs"
                data-testid={`cms-${key}-json-editor`}
              />
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
            <Button onClick={save} disabled={saving} className="bg-[#0B5FFF] hover:bg-[#0A54E0]" data-testid={`cms-${key}-save`}>
              {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving…</> : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirm */}
      <Dialog open={!!deleting} onOpenChange={(o) => !o && setDeleting(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Delete permanently?</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-600">
            This deletes <code className="px-1 bg-slate-100 rounded">{deleting?.[state.id_field]}</code> from the live database. If the item originally came from the mobile backend, the next sync will re-create it unless you remove it there too.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleting(null)}>Cancel</Button>
            <Button onClick={confirmDelete} className="bg-rose-700 hover:bg-rose-800 text-white" data-testid={`cms-${key}-delete-confirm`}>Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

export default function AdminCMS() {
  const [tab, setTab] = useState(RESOURCES[0].key);
  const activeResource = useMemo(() => RESOURCES.find((r) => r.key === tab) || RESOURCES[0], [tab]);

  return (
    <Card className="border-slate-200" data-testid="admin-cms">
      <CardContent className="p-0">
        <div className="p-4 border-b border-slate-200">
          <h3 className="font-heading font-semibold text-[#111827]">Content management</h3>
          <p className="text-xs text-slate-500 mt-1">
            Create, edit, or delete clinical content. Edits are automatically locked from the sync so your changes persist.
          </p>
        </div>
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="m-4 flex flex-wrap h-auto gap-1 bg-slate-100">
            {RESOURCES.map((r) => (
              <TabsTrigger key={r.key} value={r.key} className="text-xs" data-testid={`cms-tab-${r.key}`}>{r.label}</TabsTrigger>
            ))}
          </TabsList>
          {RESOURCES.map((r) => (
            <TabsContent key={r.key} value={r.key} className="px-4 pb-4">
              {r.key === tab && <ResourceManager resource={activeResource} />}
            </TabsContent>
          ))}
        </Tabs>
      </CardContent>
    </Card>
  );
}
